import React, { ReactNode } from 'react';
import { Checkbox, colors, Tooltip } from '@insurancemenu/imenu-components-lib';
import { Status } from 'types/emailService';
import {
  RadioButtonUnchecked,
  CheckCircle,
  InfoButton,
  InfoButtonFilled,
  RadioButtonChecked,
  Reload,
  ExchangeFile,
  DollarCircle,
  NavigationClose,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { UserType } from 'types/general';
import { observer } from 'mobx-react-lite';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { dateFormatters, capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import StageTag from 'components/common/enrollment/StageTag';
import { EnrollmentDelegate } from 'store/pages/tools/trigger-renewals/enrollmentDelegate';
import { RenewalGroupDelegate } from 'store/pages/tools/trigger-renewals/renewalGroupDelegate';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { userTypeLabels } from 'store/pages/tools/trigger-renewals/modals/manageEmailsModalDelegate';
import {
  ActionBlockStyled,
  EffectiveDateContentStyled,
  InCellDivisionBlockStyled,
  ProductLineCellContentStyled,
  StatusesListStyled,
  StatusStyled,
  TableCellStyled,
  TableRowStyled,
  TooltipContentStyled,
  FixedWidthTooltipStyled,
  EmailsTypeLabelStyled,
  EmailsByTypeStyled,
  EmailsCellContentStyled,
  ProductLineLabelStyled,
  ProductLineIconsStyled,
  EffectiveDateTextStyled,
  RestEmailsCountStyled,
} from './styles';
import MoreDropdown from './MoreDropdown';

interface TriggerRenewalsRowProps {
  groupDelegate: RenewalGroupDelegate;
}

/** The rest of the emails is shown as a counter, their statuses are available in the Manage Emails modal */
const VISIBLE_EMAIL_STATUSES_LIMIT = 2;

export const EMAIL_STATUS_ICON = Object.freeze({
  SENT: <RadioButtonUnchecked width="2.2rem" height="2.2rem" color={colors.yellow[600]} />,
  OPENED: <CheckCircle width="2.2rem" height="2.2rem" color={colors.green[600]} />,
  NEW: <InfoButton width="2.2rem" height="2.2rem" color={colors.blue[600]} />,
  // Failed Statuses
  BOUNCED: <RadioButtonChecked width="2.2rem" height="2.2rem" color={colors.red[600]} />,
  FAILED_TO_SEND: <RadioButtonChecked width="2.2rem" height="2.2rem" color={colors.orange[600]} />,
  BLOCK: <InfoButtonFilled width="2.2rem" height="2.2rem" color={colors.red[600]} />,
  INVALID: <RadioButtonChecked width="2.2rem" height="2.2rem" color={colors.red[600]} />,
  SPAM: <RadioButtonChecked width="2.2rem" height="2.2rem" color={colors.red[600]} />,
});

const getTriggerRenewalTableCell = (
  enrollmentDelegates: EnrollmentDelegate[],
  cellType: string,
  generateContent: (enrollmentDelegate: EnrollmentDelegate) => ReactNode,
): (ReactNode | null)[] => {
  return enrollmentDelegates.map((enrollmentDelegate, index) => {
    const cellKey = enrollmentDelegate.period.effectiveDate
      ? `${cellType}-${enrollmentDelegate.period.effectiveDate}`
      : index;

    return (
      <InCellDivisionBlockStyled withBorder key={cellKey}>
        {generateContent(enrollmentDelegate)}
      </InCellDivisionBlockStyled>
    );
  });
};

const getEmailsStatuses = (enrollmentDelegate: EnrollmentDelegate, type: UserType): ReactNode[] => {
  const emailsByPeriod = enrollmentDelegate.emailDelegate.getEmailsByUserType(type);
  const restEmailsCount = emailsByPeriod.length - VISIBLE_EMAIL_STATUSES_LIMIT;

  const statuses: ReactNode[] = emailsByPeriod
    .slice(0, VISIBLE_EMAIL_STATUSES_LIMIT)
    .map(({ emailAddress, userType, status }) => {
      return (
        <StatusStyled key={`${userType}_${emailAddress}_${enrollmentDelegate.period.id}_${Status[status]}`}>
          <Tooltip alignment={AlignmentTypes.BOTTOM_START} label={capitalize(Status[status].toLowerCase())}>
            {EMAIL_STATUS_ICON[status]}
          </Tooltip>
        </StatusStyled>
      );
    });

  if (restEmailsCount > 0) {
    statuses.push(
      <RestEmailsCountStyled key={`rest_emails_${type}_${enrollmentDelegate.period.id}`}>
        {`+${restEmailsCount}`}
      </RestEmailsCountStyled>,
    );
  }

  return statuses;
};

const getEmailByUserType = (enrollmentDelegate: EnrollmentDelegate, type: UserType): ReactNode => {
  const emailsStatuses = getEmailsStatuses(enrollmentDelegate, type);
  if (emailsStatuses.length === 0) {
    return null;
  }

  return (
    <EmailsByTypeStyled>
      <EmailsTypeLabelStyled>{userTypeLabels[type]}:</EmailsTypeLabelStyled>
      <StatusesListStyled>{emailsStatuses}</StatusesListStyled>
    </EmailsByTypeStyled>
  );
};

const getEmailsCellContent = (enrollmentDelegates: EnrollmentDelegate[], emailRowsCount: number): ReactNode[] => {
  return enrollmentDelegates.map((enrollmentDelegate) => {
    const aorEmails = getEmailByUserType(enrollmentDelegate, UserType.AOR);
    const adminEmails = getEmailByUserType(enrollmentDelegate, UserType.ADMIN);

    return (
      <InCellDivisionBlockStyled withBorder key={`emails_${enrollmentDelegate.period.id}`}>
        <EmailsCellContentStyled $rowsCount={emailRowsCount}>
          {enrollmentDelegate.areEmailsAvailable && (
            <>
              {aorEmails}
              {adminEmails}
            </>
          )}
        </EmailsCellContentStyled>
      </InCellDivisionBlockStyled>
    );
  });
};

const productLinesWithTooltip = (enrollmentDelegate: EnrollmentDelegate): ReactNode => {
  const missingProductsOrRates = enrollmentDelegate.missingProductsOrRates;

  return (
    <ProductLineCellContentStyled>
      <span>
        {enrollmentDelegate.allEnrollmentProductLines.map(({ id, isTerminated }, index) => (
          <React.Fragment key={id}>
            {index > 0 && ', '}
            <ProductLineLabelStyled $isTerminated={isTerminated}>
              {PRODUCT_LINES_CONTENT_MAP[id].label}
            </ProductLineLabelStyled>
          </React.Fragment>
        ))}
      </span>
      <ProductLineIconsStyled>
        {enrollmentDelegate.areExhibitsMissing && (
          <FixedWidthTooltipStyled
            alignment={AlignmentTypes.BOTTOM_START}
            label={enrollmentDelegate.missingExhibitText}
          >
            <TooltipContentStyled>
              <ExchangeFile width="2.3rem" height="2.3rem" color={colors.red[600]} />
            </TooltipContentStyled>
          </FixedWidthTooltipStyled>
        )}
        {!enrollmentDelegate.areExhibitsMissing && enrollmentDelegate.hasMismatchedProductsAndExhibits && (
          <FixedWidthTooltipStyled
            alignment={AlignmentTypes.BOTTOM_START}
            label="There is a mismatch between products & exhibits"
          >
            <TooltipContentStyled>
              <ExchangeFile width="2.3rem" height="2.3rem" color={colors.yellow[600]} />
            </TooltipContentStyled>
          </FixedWidthTooltipStyled>
        )}
        {missingProductsOrRates?.message && (
          <FixedWidthTooltipStyled alignment={AlignmentTypes.BOTTOM_START} label={missingProductsOrRates.message}>
            <TooltipContentStyled>
              <DollarCircle
                width="2.2rem"
                height="2.2rem"
                color={missingProductsOrRates.type === 'WARNING' ? colors.yellow[600] : colors.red[600]}
              />
            </TooltipContentStyled>
          </FixedWidthTooltipStyled>
        )}
        {enrollmentDelegate.terminatedProductLinesText && (
          <FixedWidthTooltipStyled
            alignment={AlignmentTypes.BOTTOM_START}
            label={enrollmentDelegate.terminatedProductLinesText}
          >
            <TooltipContentStyled>
              <NavigationClose width="2.4rem" height="2.4rem" color={colors.red[600]} />
            </TooltipContentStyled>
          </FixedWidthTooltipStyled>
        )}
      </ProductLineIconsStyled>
      <br />
    </ProductLineCellContentStyled>
  );
};

const getStage = (enrollmentDelegate: EnrollmentDelegate): ReactNode => {
  return <StageTag stage={enrollmentDelegate.stage} />;
};

const getEffectiveDate = (enrollmentDelegate: EnrollmentDelegate): ReactNode => {
  const rateGuaranteeEarlierThanEffectiveDateText = enrollmentDelegate.pLinesWithRateGuaranteeEarlierThanEffectiveDate
    .map((line) => `${line.name} – ${line.rateGuaranteeEndDate}`)
    .join(', ');
  return (
    <EffectiveDateContentStyled>
      <EffectiveDateTextStyled>
        {dateFormatters.normDateToShortAmericanDate(enrollmentDelegate.period.effectiveDate)}
      </EffectiveDateTextStyled>
      {enrollmentDelegate.pLinesWithRateGuaranteeEarlierThanEffectiveDate.length > 0 && (
        <FixedWidthTooltipStyled
          alignment={AlignmentTypes.BOTTOM_START}
          label={`Rate guarantee ends for: ${rateGuaranteeEarlierThanEffectiveDateText}`}
        >
          <TooltipContentStyled>
            <Reload width="2rem" height="2rem" color={colors.blue[600]} />
          </TooltipContentStyled>
        </FixedWidthTooltipStyled>
      )}
    </EffectiveDateContentStyled>
  );
};

const TriggerRenewalsRow: React.FC<TriggerRenewalsRowProps> = observer(({ groupDelegate }) => {
  const { enrollmentDelegates, group, emailRowsCount } = groupDelegate;
  const { id: groupId, name, agency } = group;
  const {
    batchActionDelegate: { isPeriodSelected, updateSelectedPeriodsList },
  } = useTriggerRenewalsPageStore();

  const hasSelectedPeriodInGroup = enrollmentDelegates.find((enrollmentDelegate) => {
    return isPeriodSelected(enrollmentDelegate.period.id);
  });

  const moreOptionsDropdowns = (delegates: EnrollmentDelegate[]): ReactNode[] => {
    return delegates.map((enrollmentDelegate) => {
      const period = enrollmentDelegate.period;
      const isPeriodChecked = isPeriodSelected(period.id);
      const isCheckboxDisabled = !enrollmentDelegate.isAvailableForBatchActions;
      const checkbox = (
        <Checkbox
          iconSize="2.4rem"
          label=""
          checked={isPeriodChecked}
          disabled={isCheckboxDisabled}
          highlightColor={colors.blue[600]}
          onChange={(): void => {
            updateSelectedPeriodsList(period, groupId);
          }}
        />
      );

      return (
        <InCellDivisionBlockStyled withBorder key={`dropdown_${period.id}`}>
          <ActionBlockStyled>
            <MoreDropdown group={group} currentEnrollmentDelegate={enrollmentDelegate} />
            {isCheckboxDisabled ? (
              <Tooltip label={enrollmentDelegate.batchActionsUnavailableReason} alignment={AlignmentTypes.BOTTOM_END}>
                <span>{checkbox}</span>
              </Tooltip>
            ) : (
              checkbox
            )}
          </ActionBlockStyled>
        </InCellDivisionBlockStyled>
      );
    });
  };

  const effectiveDatesCellContent = getTriggerRenewalTableCell(enrollmentDelegates, 'effectiveDate', getEffectiveDate);
  const productLinesCellContent = getTriggerRenewalTableCell(
    enrollmentDelegates,
    'productLines',
    productLinesWithTooltip,
  );
  const stagesCellContent = getTriggerRenewalTableCell(enrollmentDelegates, 'stage', getStage);

  const emailsCellContent = getEmailsCellContent(enrollmentDelegates, emailRowsCount);
  const moreOptionsCellContent = moreOptionsDropdowns(enrollmentDelegates);

  return (
    <TableRowStyled $isSelected={!!hasSelectedPeriodInGroup} data-selected={!!hasSelectedPeriodInGroup} key={groupId}>
      <TableCellStyled width="8rem">{agency.name}</TableCellStyled>
      <TableCellStyled width="8rem">{name}</TableCellStyled>
      <TableCellStyled width="6rem">{effectiveDatesCellContent}</TableCellStyled>
      <TableCellStyled width="7rem">{productLinesCellContent}</TableCellStyled>
      <TableCellStyled width="5rem">{stagesCellContent}</TableCellStyled>
      <TableCellStyled width="6rem">{emailsCellContent}</TableCellStyled>
      <TableCellStyled width="6rem">{moreOptionsCellContent}</TableCellStyled>
    </TableRowStyled>
  );
});

export default TriggerRenewalsRow;
